import React, { useEffect, useState, useCallback } from 'react';
import { makeStyles, Box, useTheme } from '@material-ui/core';
import ChatLayout from '../utilities/layouts/ChatLayout';
import { COLOR_GREY, COLOR_WHITE } from '../utilities/MaterialWatchman';
import ChatWelcomePage from './ChatWelcomePage';
import { MobileNavBarWithInfo } from '../utilities/NavBar/MobileNavBar';
import { ReactComponent as SpinnerIcon } from '../../resources/images/spinner-icon.svg';
import ChatConnectionErrorPage from './ChatConnectionErrorPage';
import { loadSessionStorage, persistLogin } from '../../utils/ClientSessionHandler';
import { DELIVERY_STATE } from './MessageEvent';
import { SubPageHeading } from '../home/TaskHome';
import { styleBreakMobile, styleBreakSmallMobile, styleBreakDesktop } from '../utilities/layouts/Globals';
import clsx from 'clsx';
import Chat, { MATRIX_SYNC_STATE, updateFullyReadMarker, MATRIX_EVENT_TYPE, setupClientAndListeners, maxChatWidth } from '../../utils/ChatModule';
import { connect, useDispatch } from 'react-redux';
import { applyHasAccessedChatOnce, setAcknowledgeFirstTimeChatAccess, updateUnreadMessages, setMaximumUnreadMessageCount } from '../../actions';
import * as ProfileAPI from '../../apis/profile';
import * as actions from '../../actions/actionTypes';
import { call, put, select, takeLatest } from 'redux-saga/effects';
import { getUserId } from '../../selectors';
import moment from 'moment';
import { getClinicHoursStatus } from '../utilities/commonUtilities/dateTools';
import { DesktopChatNavBar } from '../utilities/NavBar/DesktopNavBar';
import { getUserCredentials as getCredentials } from '../../apis/chathub';

const useStyles = makeStyles(
    theme => ({
        chatPageContainer: {
            backgroundColor: COLOR_WHITE,
            [styleBreakMobile(theme)]: {
                height: '100vh',
            },
            [styleBreakDesktop(theme)]: {
                position: 'fixed',
                right: '0.8%',
                top: '26.94%',
                bottom: '0%',
                width: maxChatWidth(theme),
                zIndex: 10000,
                borderRadius: '10px 10px 0px 0px',
                boxShadow: '0px 4px 10px rgba(0, 0, 0, 0.25)',
            },
        },
        chatPageContent: {
            overflowY: 'auto',
            paddingBottom: theme.spacing(8),
            [styleBreakMobile(theme)]: {
                paddingTop: theme.spacing(7.25),
                maxHeight: '100vh',
                height: '100vh',
            },
            [styleBreakDesktop(theme)]: {
                height: '100%',
            },
        },
        chatPageContentWithBanner: {
            paddingTop: theme.spacing(23),
        },
        loadingChat: {
            height: '100%',
            width: '100%',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            flexDirection: 'column',
        },
        spinner: {
            animation: 'flashing 1s linear infinite',
            marginBottom: theme.spacing(3.125),
            width: theme.spacing(13.5),
            height: theme.spacing(13.5),
            '& svg': {
                transform: 'rotate(-15deg)',
            },
        },
        connectingMessage: {
            fontWeight: 700,
            fontSize: theme.spacing(2.625),
            lineHeight: '27px',
            letterSpacing: '0.24px',
        },
        chatPageContentBox: {
            overflowY: 'auto',
            flexGrow: 1,
        },
    }),
    { index: 1 }
);

export function ChatPageContent({
    loginOpts,
    hasAccessedChatOnce,
    setAcknowledgeChat,
    updateUnreadMessageCount,
    setMaxUnreadMessageCount,
    desktopChat = false,
    setChatBarOpen = null,
}) {
    const classes = useStyles();
    const dispatch = useDispatch();
    const [showingInfo, setShowingInfo] = useState(false);

    const acknowledgeChat = state => {
        if (!hasAccessedChatOnce) {
            setAcknowledgeChat();

            const login = loadSessionStorage().login;

            if (login) {
                const newlogin = {
                    ...login,
                    session: {
                        ...login?.session,
                        extraFields: {
                            ...login?.session?.extraFields,
                            // follow extraFields Hashmap<String>
                            hasAccessedChatOnce: 'true',
                        },
                    },
                };

                persistLogin(newlogin);
            }
        }
        setShowingInfo(false);
    };

    const toggleShowingInfo = () => {
        setShowingInfo(!showingInfo);
    };

    // read session extraFields the first time on login.
    // all other re-renders/mapStateToProps will
    // be reading the updated store (after localStorage has been updated)
    useEffect(() => {
        dispatch(applyHasAccessedChatOnce(loadSessionStorage().login?.session?.extraFields?.hasAccessedChatOnce));
    }, [dispatch]);

    return (
        <Box className={classes.chatPageContainer} display="flex" flexDirection="column">
            {desktopChat ? (
                <Box order={1} flexBasis={'37.85px'} width={'100%'} zIndex={2}>
                    <DesktopChatNavBar infoClickHandler={toggleShowingInfo} setChatBarOpen={setChatBarOpen} />
                </Box>
            ) : (
                <Box order={1}>
                    <MobileNavBarWithInfo headerTitle="Chat" infoClickHandler={toggleShowingInfo} />
                </Box>
            )}
            {!hasAccessedChatOnce || showingInfo ? (
                <Box order={2} className={classes.chatPageContentBox}>
                    <ChatWelcomePage onAccept={acknowledgeChat} />
                </Box>
            ) : (
                <Box order={2} className={classes.chatPageContentBox} flexBasis={'auto'}>
                    <ChatPageContentChild
                        loginOpts={loginOpts}
                        hasAccessedChatOnce={hasAccessedChatOnce}
                        updateUnreadMessageCount={updateUnreadMessageCount}
                        setMaxUnreadMessageCount={setMaxUnreadMessageCount}
                    />
                </Box>
            )}
        </Box>
    );
}

export const ChatBanners = {
    CONNECTION_ERROR: 'CONNECTION_ERROR',
    CLINIC_HOURS: 'CLINIC_HOURS',
};

export function OverrideSubPageHeading({ chatBanner, greetingText, instructionText }) {
    const connectionErrorTitle = 'Connection Error';
    const connectionErrorText = 'We were unable to connect you to chat, an internet connection is required.';

    const clinicHoursTitle = 'Clinic Closed';
    const clinicHoursText = 'The clinic is currently closed. There may be a delay in reviewing your messages.';

    const theme = useTheme();
    const stylesOverride = {
        subsection: {
            position: 'fixed',
            zIndex: 1,
            [styleBreakMobile(theme)]: {
                top: theme.spacing(6.25),
                width: '100%',
            },
            [styleBreakDesktop(theme)]: {
                borderRadius: '15px 15px 0px 0px',
                right: '0.8%',
                top: '26.94%',
                overflowX: 'hidden',
                width: theme.spacing(41.5),
            },
        },
        subPageBox: {
            boxShadow: `0 1px ${theme.spacing(0.5)}px 0 ${COLOR_GREY.main}`,
            [styleBreakSmallMobile(theme)]: {
                width: '150%',
                transform: 'translateY(-75%)',
                top: theme.spacing(11),
            },
            [styleBreakMobile(theme)]: {
                transform: 'translateY(-46%)',
                width: '150%',
                top: 0,
            },
        },
        greetingTextTypography: {
            [styleBreakMobile(theme)]: {
                marginBottom: theme.spacing(1.25),
            },
            [styleBreakDesktop(theme)]: {
                marginBottom: theme.spacing(0.75),
            },
        },
        subPageBoxBigScreen: {
            top: 0,
            transform: 'translateY(0%)',
            width: '150%',
            height: '90%',
        },
        instructionBox: {
            paddingTop: theme.spacing(2),
            [styleBreakDesktop(theme)]: {
                paddingTop: theme.spacing(7.25),
                paddingBottom: theme.spacing(6.25),
                marginLeft: theme.spacing(2.25),
                marginRight: theme.spacing(2.25),
            },
        },
    };

    switch (chatBanner) {
        case ChatBanners.CONNECTION_ERROR:
            greetingText = connectionErrorTitle;
            instructionText = connectionErrorText;
            break;
        case ChatBanners.CLINIC_HOURS:
            greetingText = clinicHoursTitle;
            instructionText = clinicHoursText;
            break;
        default:
            return null;
    }

    return <SubPageHeading stylesOverride={stylesOverride} greetingText={greetingText} instructionText={instructionText} />;
}

function ChatPageContentChild({ hasAccessedChatOnce, loginOpts, updateUnreadMessageCount, setMaxUnreadMessageCount }) {
    const [events, setEvents] = useState([]);
    const [selfUserId, setSelfUserId] = useState(null);
    const [otherUsers, setOtherUsers] = useState([]);
    const [roomId, setRoomId] = useState(null);
    const [typing, setTyping] = useState('');
    const [loading, setLoading] = useState(false);
    const [hasSetupChatListeners, setHasSetupChatListeners] = useState(false);
    const [client, setClient] = useState(null);
    const [errorLoading, setErrorLoading] = useState(false);
    const [showConnectionErrorBanner, setShowConnectionErrorBanner] = useState(false);
    const [showClinicHoursBanner, setShowClinicHoursBanner] = useState(false);
    const [reconnecting, setReconnecting] = useState(false);

    const classes = useStyles();

    const setupChatClient = useCallback(async () => {
        setErrorLoading(false);
        setLoading(true);

        function callback({ client, events, otherUsers, userId, roomId }) {
            setClient(client);
            setLoading(false);
            setSelfUserId(userId);
            setOtherUsers(otherUsers);
            setRoomId(roomId);
            setEvents(events);
        }

        try {
            if(Chat?.getClientSyncState() !== MATRIX_SYNC_STATE.PREPARED && Chat?.getClientSyncState() !== MATRIX_SYNC_STATE.SYNCING) {
                getCredentials();
                await Chat.init(updateUnreadMessageCount, setMaxUnreadMessageCount, callback, loginOpts);
            } else {
                setupClientAndListeners(null, updateUnreadMessageCount, setMaxUnreadMessageCount, callback);
            }
            setReconnecting(false);
        } catch (e) {
            // show error message on failed connection.
            // with option to reconnect.
            setErrorLoading(true);
        }
    }, [loginOpts, updateUnreadMessageCount, setMaxUnreadMessageCount]);

    const sendMessage = async msgObj => {
        try {
            await client.sendEvent(roomId, MATRIX_EVENT_TYPE.MESSAGE, msgObj, '');
        } catch (e) {
            console.error(e);
        }
    };

    // any UNSENT message will be queued up to be sent, keeping its order in the timeline.
    const handleResend = async event => {
        try {
            await client.resendEvent(event);
        } catch (e) {
            console.error(e);
        }
    };

    useEffect(() => {
        if (hasAccessedChatOnce) {
            setupChatClient();
        }
    }, [setupChatClient, hasAccessedChatOnce]);

    const timelineEventHandler = useCallback(
        (event, room, toStartOfTimeline) => {
            if (room.roomId === roomId) {
                const newtimeline = [...room.timeline];
                setEvents(newtimeline);
            }
        },
        [roomId, setEvents]
    );

    const typingEventHandler = useCallback(
        (event, member, x) => {
            if (member.typing && member.roomId === roomId) {
                setTyping(`${member.name} is typing...`);
            } else {
                setTyping(``);
            }
        },
        [setTyping, roomId]
    );

    const localEchoUpdatedHandler = useCallback(
        (event, room) => {
            if (event?.status === DELIVERY_STATE.SENT || event?.status === DELIVERY_STATE.NOT_SENT) {
                const timeline = [...room.timeline];
                setEvents(timeline);
            }
        },
        [setEvents]
    );

    const syncHandler = useCallback(
        (state, prevState, data) => {
            if (state === MATRIX_SYNC_STATE.ERROR) {
                setShowConnectionErrorBanner(true);
            } else {
                setShowConnectionErrorBanner(false);
            }
        },
        [setShowConnectionErrorBanner]
    );

    useEffect(() => {
        if (hasAccessedChatOnce && client && roomId && !hasSetupChatListeners) {
            client.on('Room.localEchoUpdated', localEchoUpdatedHandler);
            client.on('Room.timeline', timelineEventHandler);
            // Listen for typing changes
            client.on('RoomMember.typing', typingEventHandler);
            client.on('sync', syncHandler);

            setHasSetupChatListeners(true);
        }

        return () => {
            if (client && hasSetupChatListeners) {
                client.removeListener('Room.localEchoUpdated', localEchoUpdatedHandler);
                client.removeListener('Room.timeline', timelineEventHandler);
                client.removeListener('RoomMember.typing', typingEventHandler);
                client.removeListener('sync', syncHandler);

                setHasSetupChatListeners(false);
            }
        };
    }, [roomId, client, hasSetupChatListeners, hasAccessedChatOnce, localEchoUpdatedHandler, timelineEventHandler, typingEventHandler, syncHandler]);

    useEffect(() => {
        let currentTime = moment();
        let { clinicIsClosed, timeUntilUpdate } = getClinicHoursStatus(currentTime);
        setShowClinicHoursBanner(clinicIsClosed);
        var intervalUntilUpdate = setTimeout(setShowClinicHoursBanner, timeUntilUpdate, !showClinicHoursBanner);
        return () => clearTimeout(intervalUntilUpdate);
    }, [showClinicHoursBanner]);

    useEffect(() => {
        if (hasAccessedChatOnce && hasSetupChatListeners && !loading) {
            let latestMessage = events[events.length - 1];
            var timeToUpdateReadReceipts = setTimeout(updateFullyReadMarker, 3000, latestMessage, roomId, client, setMaxUnreadMessageCount);
        }
        return () => clearTimeout(timeToUpdateReadReceipts);
    }, [hasAccessedChatOnce, client, roomId, hasSetupChatListeners, loading, events, selfUserId, setMaxUnreadMessageCount]);

    // Delete an UNSENT message. Just remove the event from the Timeline
    // Deleting a SENT message will need to use client.redactEvent()
    const handleDelete = async event => {
        try {
            client.cancelPendingEvent(event);
        } catch (e) {
            console.error(e);
        }
    };

    const ViewWrapper = ({ children }) => (
        <>
            <div
                className={clsx(classes.chatPageContent, {
                    [classes.chatPageContentWithBanner]: showConnectionErrorBanner || showClinicHoursBanner,
                })}
            >
                {showConnectionErrorBanner && <OverrideSubPageHeading chatBanner={ChatBanners.CONNECTION_ERROR} />}
                {showClinicHoursBanner && !showConnectionErrorBanner && !loading && <OverrideSubPageHeading chatBanner={ChatBanners.CLINIC_HOURS} />}
                {children}
            </div>
        </>
    );

    if (errorLoading) {
        return (
            <ChatConnectionErrorPage
                onReconnect={() => {
                    setReconnecting(true);
                    setupChatClient();
                    setTimeout(setReconnecting, 5000, false);
                }}
                reconnecting={reconnecting}
            />
        );
    }

    if (!events?.length) {
        return (
            <div className={classes.loadingChat}>
                <SpinnerIcon className={classes.spinner} />
                <span className={classes.connectingMessage}>Connecting</span>
            </div>
        );
    }

    return (
        <ViewWrapper>
            <ChatLayout
                events={events}
                selfUserId={selfUserId}
                otherUsers={otherUsers}
                typing={typing}
                client={client}
                showingErrorBanner={showConnectionErrorBanner}
                onSendMessage={sendMessage}
                onResend={handleResend}
                onDelete={handleDelete}
            />
        </ViewWrapper>
    );
}

const mapStateToProps = state => {
    return {
        hasAccessedChatOnce: state?.profile?.hasAccessedChatOnce === 'true', //state?.login?.session?.extraFields?.hasAccessedChatOnce === "true",
    };
};
const mapDispatchToProps = dispatch => {
    const setAcknowledgeChat = () => {
        dispatch(setAcknowledgeFirstTimeChatAccess());
    };
    const updateUnreadMessageCount = unreadMessageCount => {
        dispatch(updateUnreadMessages(unreadMessageCount));
    };
    const setMaxUnreadMessageCount = maximumUnreadMessageCount => {
        dispatch(setMaximumUnreadMessageCount(maximumUnreadMessageCount));
    };
    return {
        setAcknowledgeChat,
        updateUnreadMessageCount,
        setMaxUnreadMessageCount,
    };
};

export default connect(mapStateToProps, mapDispatchToProps)(ChatPageContent);

export function* acknowledgeFirstTimeChatAccessSaga() {
    try {
        const userId = yield select(getUserId);
        const response = yield call(ProfileAPI.setAcknowledgeFirstTimeChatAccess, userId);

        yield put(applyHasAccessedChatOnce(response?.data?.toString()));
    } catch (error) {
        console.dir(error);
    }
}

export function* watchAcknowledgeFirstTimeChatAccessSaga() {
    yield takeLatest(actions.CHAT_ACKNOWLEDGE_FIRST_TIME_ACCESS, acknowledgeFirstTimeChatAccessSaga);
}
